/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * What a bound column will and will not accept.
 *
 * The platform has always known how long a value may be and which characters
 * it allows — `max_length`, `allowed_characters` and `validation_rules` sit on
 * every column. Nothing read them, so each form that wanted a limit restated
 * it in its own code, and the limit drifted from the column it was describing.
 *
 * The builder's Data tab now copies those rules onto the field when it is
 * mapped, and this is where they are applied. Kept apart from
 * FormBuilderSection for the same reason as formSubmitFeedback: these are
 * decisions about meaning, and they are worth testing without a rendering tree
 * around them.
 *
 * The database remains the authority — over-length and NOT NULL are refused at
 * the column too, and come back humanised. This is the earlier, kinder half of
 * the same rule, not a replacement for it.
 */

/** Field types a character cap means anything on, and that accept `maxLength`. */
export const LENGTH_CAPPED_TYPES = new Set(['text', 'email', 'password', 'textarea']);

/**
 * The domains an email field accepts, or null when it accepts any.
 *
 * NOT part of `field.rules`, and deliberately so. `rules` is copied off the
 * bound column by the builder's Data tab and REPLACED wholesale every time the
 * mapping changes, so an allowlist typed by an author and parked in there would
 * vanish the next time somebody re-mapped the field. This one is the author's,
 * so it lives on the field itself.
 *
 * Empty and `*` both mean "any domain" — an author who clears the box, and one
 * who says "anything" out loud, mean the same thing and must not get different
 * forms. A single `*` anywhere in the list opens the whole list for the same
 * reason: `gmail.com, *` cannot coherently mean anything narrower.
 */
export function allowedDomains(field) {
  if (!field || field.type !== 'email') return null;

  const raw = field.allowedDomains;
  const list = (Array.isArray(raw) ? raw : String(raw ?? '').split(','))
    // `@company.com` is what an author types when the label says "domain", and
    // refusing it would be pedantry about an @ the field already implies.
    .map((entry) => String(entry ?? '').trim().toLowerCase().replace(/^@+/, ''))
    .filter(Boolean);

  if (!list.length || list.includes('*')) return null;
  return list;
}

/** "@a.com", "@a.com or @b.com", "@a.com, @b.com or @c.com". */
function readableDomains(list) {
  const marked = list.map((domain) => `@${domain}`);
  if (marked.length === 1) return marked[0];
  return `${marked.slice(0, -1).join(', ')} or ${marked[marked.length - 1]}`;
}

/**
 * Why this address is not allowed here, or null when it is.
 *
 * Exact match on the part after the last @: an allowlist of `company.com` does
 * NOT admit `mail.company.com`. An author who wants a subdomain lists it, which
 * is the reading where one typed line never permits an address they did not
 * picture.
 *
 * Shape is not judged here — `validateFields` has already refused anything that
 * is not an email address, and complaining about the domain of "asdf" would
 * answer a question nobody asked.
 */
export function domainError(field, value) {
  const list = allowedDomains(field);
  if (!list) return null;
  if (typeof value !== 'string' || value === '') return null;

  const at = value.lastIndexOf('@');
  if (at === -1) return null;

  const domain = value.slice(at + 1).trim().toLowerCase();
  if (list.includes(domain)) return null;

  // Names the addresses that WOULD work rather than the one that did not: the
  // runner cannot guess the list, and "invalid email" on a perfectly valid
  // address reads as the form being broken.
  return `Only ${readableDomains(list)} addresses are allowed`;
}

/**
 * The first rule this value breaks, or null when it breaks none.
 *
 * A field with no rules — every form built before this existed — returns null
 * immediately and behaves exactly as it did.
 *
 * Emptiness is deliberately not judged here. Whether a blank answer is allowed
 * is `field.required`, which the caller has already decided on; a rule about
 * length or shape has nothing to say about a value nobody gave.
 */
export function ruleError(field, value) {
  const rules = field && field.rules;
  if (!rules) return null;
  if (typeof value !== 'string' || value === '') return null;

  if (rules.maxLength && value.length > rules.maxLength) {
    return `${field.label} must be at most ${rules.maxLength} characters`;
  }
  if (rules.minLength && value.length < rules.minLength) {
    return `${field.label} must be at least ${rules.minLength} characters`;
  }
  if (rules.pattern) {
    // `allowed_characters` is typed by an administrator and is not guaranteed
    // to compile. An unusable pattern must not block a submission that is
    // otherwise fine, so a bad one is skipped rather than thrown.
    let re = null;
    try { re = new RegExp(rules.pattern); } catch (_) { re = null; }
    if (re && !re.test(value)) {
      return rules.patternMessage || `${field.label} contains characters that are not allowed`;
    }
  }
  if (rules.dataType === 'integer' && !/^-?\d+$/.test(value)) {
    return `Enter a whole number for ${field.label}`;
  }
  return null;
}

/**
 * The `maxLength` to hand the input, or undefined.
 *
 * Stops typing at the column's limit so a long paste is visibly truncated
 * rather than silently losing its tail on save. Only on the types that take
 * the attribute — a select or a date carrying `maxLength` is a DOM warning.
 */
export function inputMaxLength(field) {
  if (!field || !field.rules || !LENGTH_CAPPED_TYPES.has(field.type)) return undefined;
  return field.rules.maxLength || undefined;
}
